/**
 * Action grouping utility for proposal actions.
 *
 * Groups related actions by their dependency chains (e.g., create + associate).
 * This enables better visual presentation of related CRM operations.
 */

import type {
  ActionPayload,
  ProposedAction,
  ProposedActionTypeName,
} from '../core/types';
import {
  isAssociationPayload,
  isDestructiveCrmPayload,
  isObjectCreatedPayload,
  isObjectMergedPayload,
  isTimelineEventPayload,
} from '../core/types';
import { mergeActions } from './actionMerging';

/**
 * Represents a group of related actions.
 */
export interface ActionGroup {
  /** Unique identifier for the group */
  id: string;
  /** Actions in this group, ordered by execution_order */
  actions: ProposedAction[];
  /** Whether this group contains destructive operations (delete, merge) */
  isDestructive: boolean;
  /** The primary (first) action in the chain */
  primaryAction: ProposedAction;
}

/**
 * Extract entity IDs from an action payload.
 * Returns the entity_id of the action and any referenced entity_ids.
 */
function extractEntityIds(payload: ActionPayload): Set<string> {
  const ids = new Set<string>();

  if (!isTimelineEventPayload(payload)) {
    return ids;
  }

  // Always include the action's own entity_id
  ids.add(payload.entity_id);

  // Add referenced entity IDs based on action type
  if (isAssociationPayload(payload)) {
    ids.add(payload.payload.target_entity_id);
  } else if (isObjectMergedPayload(payload)) {
    ids.add(payload.payload.source_entity_id);
  }

  return ids;
}

/**
 * Build a graph of entity ID relationships.
 * Returns a map of entity_id -> set of connected entity_ids.
 */
function buildDependencyGraph(
  actions: ProposedAction[]
): Map<string, Set<string>> {
  const graph = new Map<string, Set<string>>();

  for (const action of actions) {
    const ids = extractEntityIds(action.payload);

    // Add all IDs to graph if not present
    for (const id of ids) {
      if (!graph.has(id)) {
        graph.set(id, new Set());
      }
    }

    // Connect all IDs in this action (they're related)
    const idArray = Array.from(ids);
    for (let i = 0; i < idArray.length; i++) {
      for (let j = i + 1; j < idArray.length; j++) {
        graph.get(idArray[i])?.add(idArray[j]);
        graph.get(idArray[j])?.add(idArray[i]);
      }
    }
  }

  return graph;
}

/**
 * Find all connected components in the dependency graph using BFS.
 * Returns sets of connected entity IDs.
 */
function findConnectedComponents(
  graph: Map<string, Set<string>>
): Set<string>[] {
  const visited = new Set<string>();
  const components: Set<string>[] = [];

  for (const startNode of graph.keys()) {
    if (visited.has(startNode)) {
      continue;
    }

    // BFS to find all connected nodes
    const component = new Set<string>();
    const queue = [startNode];

    while (queue.length > 0) {
      const node = queue.shift();
      if (node === undefined || visited.has(node)) {
        continue;
      }

      visited.add(node);
      component.add(node);

      const neighbors = graph.get(node) || new Set();
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          queue.push(neighbor);
        }
      }
    }

    components.push(component);
  }

  return components;
}

/**
 * Group actions by their dependency chains.
 *
 * Actions are considered related if they reference the same entity IDs:
 * - A create action outputs an entity_id
 * - An associate action references source and target entity_ids
 * - A merge action references source and target entity_ids
 *
 * Independent actions (like emails) each form their own group.
 *
 * @param actions - Array of proposed actions
 * @returns Array of action groups
 */
export function groupActionsByDependency(
  actions: ProposedAction[]
): ActionGroup[] {
  if (actions.length === 0) {
    return [];
  }

  // Separate CRM actions from non-CRM actions
  const crmActions: ProposedAction[] = [];
  const nonCrmActions: ProposedAction[] = [];

  for (const action of actions) {
    if (isTimelineEventPayload(action.payload)) {
      crmActions.push(action);
    } else {
      nonCrmActions.push(action);
    }
  }

  // Build dependency graph for CRM actions
  const graph = buildDependencyGraph(crmActions);

  // Find connected components
  const components = findConnectedComponents(graph);

  // Map each action to its component
  const actionToComponent = new Map<ProposedAction, Set<string>>();
  for (const action of crmActions) {
    const ids = extractEntityIds(action.payload);
    // Find which component contains any of this action's IDs
    for (const component of components) {
      for (const id of ids) {
        if (component.has(id)) {
          actionToComponent.set(action, component);
          break;
        }
      }
      if (actionToComponent.has(action)) {
        break;
      }
    }
  }

  // Group actions by component
  const componentToActions = new Map<Set<string>, ProposedAction[]>();
  for (const [action, component] of actionToComponent) {
    if (!componentToActions.has(component)) {
      componentToActions.set(component, []);
    }
    componentToActions.get(component)?.push(action);
  }

  // Create groups for CRM action chains
  const groups: ActionGroup[] = [];
  let groupCounter = 0;

  for (const [, actionsInComponent] of componentToActions) {
    // Sort by execution order
    actionsInComponent.sort((a, b) => a.execution_order - b.execution_order);

    // Check if any action is destructive
    const isDestructive = actionsInComponent.some(action =>
      isDestructiveCrmPayload(action.payload)
    );

    groups.push({
      id: `group-${groupCounter++}`,
      actions: actionsInComponent,
      isDestructive,
      primaryAction: actionsInComponent[0],
    });
  }

  // Create individual groups for non-CRM actions
  for (const action of nonCrmActions) {
    groups.push({
      id: `group-${groupCounter++}`,
      actions: [action],
      isDestructive: false,
      primaryAction: action,
    });
  }

  // Sort groups by the execution order of their primary action
  groups.sort(
    (a, b) => a.primaryAction.execution_order - b.primaryAction.execution_order
  );

  return groups;
}

// ============================================================================
// Homogeneous CRM Group Detection (for table view)
// ============================================================================

/**
 * Minimum number of actions required to show table view
 */
export const MIN_TABLE_VIEW_ACTIONS = 5;

/**
 * Display-order rank for action groups: email groups first, then calendar
 * groups, then all other groups. Lower rank sorts earlier. Any action type
 * not listed here uses DEFAULT_GROUP_SORT_RANK. Display-only — does not affect
 * backend execution order.
 */
const PINNED_GROUP_SORT_RANK: Partial<Record<ProposedActionTypeName, number>> =
  {
    gmail_send: 0,
    calendar_event_create: 1,
  };
const DEFAULT_GROUP_SORT_RANK = 2;

/**
 * Represents a typed action group for display.
 * Groups can be rendered as either table or accordion view.
 */
export interface TypedActionGroup {
  /** Unique identifier for the group */
  id: string;
  /** Actions in this group, ordered by execution_order */
  actions: ProposedAction[];
  /** How this group should be rendered */
  viewType: 'table' | 'accordion' | 'mixed';
  /** CRM event type name (e.g., 'ahoy_object_created') - only for table groups */
  actionTypeName?: string;
  /** Entity type name (e.g., 'ahoy_contact') - only for table groups */
  entityTypeName?: string;
  /** Property keys for table columns (union of all action properties) - only for table groups */
  propertyKeys?: string[];
  /** Whether this group contains destructive operations */
  isDestructive: boolean;
}

/**
 * Get the CRM event type from a timeline event payload.
 * Returns null for non-CRM actions.
 */
function getCrmEventType(payload: ActionPayload): string | null {
  if (!isTimelineEventPayload(payload)) {
    return null;
  }
  return payload.event_type_name;
}

/**
 * Get the entity type from a timeline event payload.
 * Returns null for non-entity actions.
 */
function getEntityType(payload: ActionPayload): string | null {
  if (!isTimelineEventPayload(payload)) {
    return null;
  }
  return payload.entity_type_name;
}

/**
 * Check if a group of actions is homogeneous CRM actions.
 * A group is homogeneous if:
 * - All actions have the same CRM event type (e.g., all 'ahoy_object_created')
 * - There are MIN_TABLE_VIEW_ACTIONS (5) or more actions
 *
 * @param actions - Array of proposed actions
 * @returns true if the group qualifies for table view
 */
export function isHomogeneousCrmGroup(actions: ProposedAction[]): boolean {
  if (actions.length < MIN_TABLE_VIEW_ACTIONS) {
    return false;
  }

  // Get event type of first action
  const firstEventType = getCrmEventType(actions[0].payload);
  if (firstEventType === null) {
    // Non-CRM action, can't be homogeneous CRM group
    return false;
  }

  // Check all actions have the same event type
  for (const action of actions) {
    const eventType = getCrmEventType(action.payload);
    if (eventType !== firstEventType) {
      return false;
    }
  }

  return true;
}

/**
 * Extract the union of all property keys from a group of create actions.
 * Properties are returned in order of first appearance.
 *
 * @param actions - Array of proposed actions
 * @returns Array of property keys in order of first appearance
 */
export function getPropertyUnion(actions: ProposedAction[]): string[] {
  const seenKeys = new Set<string>();
  const orderedKeys: string[] = [];

  for (const action of actions) {
    // Only extract properties from create actions
    if (!isObjectCreatedPayload(action.payload)) {
      continue;
    }

    const { properties } = action.payload.payload;
    for (const key of Object.keys(properties)) {
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        orderedKeys.push(key);
      }
    }
  }

  return orderedKeys;
}

/**
 * Get a unique key for grouping CRM actions.
 * Groups by both event type and entity type to ensure homogeneity.
 */
function getCrmGroupKey(payload: ActionPayload): string | null {
  if (!isTimelineEventPayload(payload)) {
    return null;
  }
  return `${payload.event_type_name}::${payload.entity_type_name}`;
}

/**
 * Check if actions should use mixed table view.
 * Returns true when:
 * - Multiple different CRM event types exist (e.g., CREATE + LINK mixed together), OR
 * - All actions are non-CREATE CRM actions (event_type_name !== 'ahoy_object_created')
 *
 * Note: This function assumes actions have already passed the 5+ threshold check.
 *
 * @param actions - Array of proposed actions (all CRM actions)
 * @returns true if actions should use mixed view
 */
function shouldUseMixedMode(actions: ProposedAction[]): boolean {
  // Get unique event types
  const eventTypes = new Set<string>();
  let hasCreateAction = false;

  for (const action of actions) {
    const eventType = getCrmEventType(action.payload);
    if (eventType !== null) {
      eventTypes.add(eventType);
      if (eventType === 'ahoy_object_created') {
        hasCreateAction = true;
      }
    }
  }

  // Multiple different CRM event types exist
  if (eventTypes.size > 1) {
    return true;
  }

  // All actions are non-CREATE CRM actions
  if (!hasCreateAction && eventTypes.size > 0) {
    return true;
  }

  return false;
}

/**
 * Group actions by their CRM type for display.
 *
 * This function determines how to render each group of actions:
 * - Homogeneous CRM groups (5+ same-type actions) → table view
 * - Other groups → accordion view (using dependency grouping)
 *
 * @param actions - Array of proposed actions
 * @returns Array of typed action groups
 */
export function groupActionsByType(
  actions: ProposedAction[]
): TypedActionGroup[] {
  if (actions.length === 0) {
    return [];
  }

  // Step 1: Separate CRM from non-CRM actions
  const allCrmActions: ProposedAction[] = [];
  const nonCrmActions: ProposedAction[] = [];

  for (const action of actions) {
    if (isTimelineEventPayload(action.payload)) {
      allCrmActions.push(action);
    } else {
      nonCrmActions.push(action);
    }
  }

  const typedGroups: TypedActionGroup[] = [];
  let groupCounter = 0;

  // Compute the number of visible rows after merging create+association chains.
  // This prevents a single create with N associations from triggering table view.
  const mergedCrmCount = mergeActions(allCrmActions).length;

  // Step 2: Decide how to handle CRM actions based on total count
  if (mergedCrmCount < MIN_TABLE_VIEW_ACTIONS) {
    // a. If total CRM actions < 5, use dependency grouping → accordion for each
    const dependencyGroups = groupActionsByDependency(allCrmActions);
    for (const depGroup of dependencyGroups) {
      typedGroups.push({
        id: `type-group-${groupCounter++}`,
        actions: depGroup.actions,
        viewType: 'accordion',
        isDestructive: depGroup.isDestructive,
      });
    }
  } else {
    // b. If total CRM actions >= 5, check if mixed mode applies
    if (shouldUseMixedMode(allCrmActions)) {
      // i. Mixed mode: Combine ALL CRM actions into ONE group with viewType: 'mixed'
      allCrmActions.sort((a, b) => a.execution_order - b.execution_order);

      typedGroups.push({
        id: `type-group-${groupCounter++}`,
        actions: allCrmActions,
        viewType: 'mixed',
        propertyKeys: undefined,
        isDestructive: allCrmActions.some(action =>
          isDestructiveCrmPayload(action.payload)
        ),
      });
    } else {
      // ii. Not mixed mode: Check homogeneity per type group
      // Group CRM actions by event type AND entity type
      const crmActionsByTypeKey = new Map<string, ProposedAction[]>();

      for (const action of allCrmActions) {
        const groupKey = getCrmGroupKey(action.payload);
        if (groupKey !== null) {
          if (!crmActionsByTypeKey.has(groupKey)) {
            crmActionsByTypeKey.set(groupKey, []);
          }
          crmActionsByTypeKey.get(groupKey)?.push(action);
        }
      }

      // Process each type group
      for (const [, crmActions] of crmActionsByTypeKey) {
        const eventType = getCrmEventType(crmActions[0].payload);

        if (
          isHomogeneousCrmGroup(crmActions) &&
          eventType === 'ahoy_object_created'
        ) {
          // Homogeneous CREATE actions → table view (property columns)
          crmActions.sort((a, b) => a.execution_order - b.execution_order);

          const entityType = getEntityType(crmActions[0].payload);

          typedGroups.push({
            id: `type-group-${groupCounter++}`,
            actions: crmActions,
            viewType: 'table',
            actionTypeName: eventType ?? undefined,
            entityTypeName: entityType ?? undefined,
            propertyKeys: getPropertyUnion(crmActions),
            isDestructive: crmActions.some(action =>
              isDestructiveCrmPayload(action.payload)
            ),
          });
        } else {
          // Not homogeneous or not CREATE actions → dependency grouping → accordion
          const dependencyGroups = groupActionsByDependency(crmActions);
          for (const depGroup of dependencyGroups) {
            typedGroups.push({
              id: `type-group-${groupCounter++}`,
              actions: depGroup.actions,
              viewType: 'accordion',
              isDestructive: depGroup.isDestructive,
            });
          }
        }
      }
    }
  }

  // Step 3: Add non-CRM actions as individual accordion groups
  for (const action of nonCrmActions) {
    typedGroups.push({
      id: `type-group-${groupCounter++}`,
      actions: [action],
      viewType: 'accordion',
      isDestructive: false,
    });
  }

  // Step 4: Sort groups so email groups render first, then calendar groups,
  // then all other groups; within each rank, preserve execution_order. This is
  // a display-only ordering — execution_order values and the backend execution
  // sequence are untouched.
  const groupSortRank = (group: TypedActionGroup): number =>
    PINNED_GROUP_SORT_RANK[group.actions[0].proposed_action_type_name] ??
    DEFAULT_GROUP_SORT_RANK;
  typedGroups.sort((a, b) => {
    const rankDiff = groupSortRank(a) - groupSortRank(b);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    return a.actions[0].execution_order - b.actions[0].execution_order;
  });

  return typedGroups;
}

/**
 * Derive human-readable plural label from entity type name.
 * @example 'ahoy_contact' -> 'contacts'
 * @example 'ahoy_company' -> 'companies'
 */
export function getEntityTypeLabel(entityTypeName: string): string {
  const match = entityTypeName.match(/^ahoy_(\w+)$/);
  if (!match) {
    return 'records';
  }
  const singular = match[1];
  // Handle special plural cases
  if (singular === 'company') {
    return 'companies';
  }
  return `${singular}s`;
}
